/**
 * #989 — Polymarket US order rows for the standalone page, in the Position
 * Tracker's Open Orders table style: the same columns a Kalshi/international
 * row has (Market · Source · Side · Type · Qty · Price · Fees · actions), the
 * same cell padding and YES/NO side badge. Every figure comes from
 * `presentUsOrder`; this file only lays it out. On the Tracker itself, US
 * orders are rows of the shared Open Orders list (#1226).
 *
 * What the venue API forces to differ stays, in the trader's words: a US
 * order's filled and remaining sizes come from the venue lifecycle (a small
 * line under Qty), and a row that is not simply resting carries one amber or
 * muted word beside its actions. Evidence vocabulary (reason codes, recorded
 * times) lives in `title` attributes, never in a cell; G1's intent id and the
 * venue's order id are the row's `data-intent-id` / `data-order-id`, for hosts
 * and tests, not a line the trader reads.
 *
 * Rows are keyed by `usOrderKey` (scope + intent id), never by label or index,
 * so a refresh or a reorder never remounts a row.
 */
import { useEffect, useReducer, useRef } from 'react';

import { isUsCancelUnconfirmedCode, US_CANCEL_UNCONFIRMED_TEXT } from '@/lib/polymarketUs/cancelOutcome';
import { formatUsUsd, usPriceCentsLabel } from '@/lib/polymarketUs/format';
import { presentUsOrder, usCancelView, usOrderKey, type UsOrderView } from '@/lib/polymarketUs/portfolioModel';
import { usOrderReasonSentence } from '@/lib/polymarketUs/reason';
import type { UsOrderRow, UsReadiness } from '@/lib/polymarketUs/types';
import { UsOrderTerms } from './usOrderTerms';
import { PortfolioVenueBadge } from './usVenueLabel';

/**
 * `YYYY-MM-DD HH:MM:SS` in UTC, so the value reads the same on every machine.
 * The date is kept: saved evidence spans days, and 23:59 yesterday must not
 * read as today. Hover text only on the rows; other surfaces print it.
 */
export function formatRecordedUtc(ms: number | null): string {
  if (ms === null) return 'Unavailable';
  const iso = new Date(ms).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
}

export const RECORDED_TITLE = "When G1's server last recorded evidence for this order. Not an exchange timestamp.";

export type UsOrderOperation = 'cancel' | 'repeat' | 'status' | 'refresh';

/** #1113 T7 item 2: what Repeat cancel is for, and why it waits. */
export const REPEAT_CANCEL_TITLE =
  'Sends another cancel for an order still open after an unconfirmed cancel. It waits 10 s so the first cancel can settle; Check status works now.';

/** #1114 T8: what Check status does (and never does). */
const CHECK_STATUS_TITLE = 'Reads the order; never cancels or resends.';

/** Why every button on a row is unavailable while another action runs. */
const WAITING_TITLE = 'Waiting for another action to finish.';

/** Whole seconds until a repeat cancel unlocks, rounded up so "in 1s" shows until it is really available. */
export const repeatSecondsLeft = (availableAtMs: number, nowMs: number) => Math.max(0, Math.ceil((availableAtMs - nowMs) / 1000));

/** What the panel hands the table so rows can offer actions. */
export type UsOrderActions = {
  readiness: UsReadiness;
  nowMs: number;
  /** The one operation in flight on this panel, if any. */
  pending: { key: string; kind: UsOrderOperation } | null;
  /** True while any operation or read is in flight; every action waits. */
  busy: boolean;
  /** #1113: the panel's clock, so a locked Repeat cancel can count down on
   * its own seconds without re-rendering the panel. Absent = no countdown ticks. */
  clock?: () => number;
  /** Last failure per row key; the row itself is never removed for it. */
  errors: ReadonlyMap<string, string>;
  run: (kind: UsOrderOperation, row: UsOrderRow) => void;
};

const PENDING_LABEL: Record<UsOrderOperation, string> = {
  cancel: 'Sending cancel…',
  repeat: 'Sending repeat cancel…',
  status: 'Checking status…',
  refresh: 'Refreshing…',
};

// Sizing and unavailable look follow OrderActionButtons; these carry words,
// not icons, because cancel state must be readable without hovering.
// Unavailable buttons use aria-disabled, not `disabled`: a disabled button
// drops keyboard focus to the page, so a keyboard user pressing Cancel would
// lose their place when the lock engages.
export const ACTION_BUTTON =
  'border-border inline-flex h-6 items-center rounded border px-2 text-[11px] transition-colors aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

/**
 * #1113 T7 item 2: Repeat cancel while locked counts down ("Repeat cancel in
 * 8s"), centred in a width reserved for its longest label so the ticks move
 * nothing. It re-renders itself on its own whole seconds (one timer, this
 * button only); the panel wakes once, at the unlock, to make it usable.
 */
function RepeatCancelButton({ label, availableAtMs, canRepeat, nowMs, clock, title, press }: {
  label: string;
  availableAtMs: number;
  canRepeat: boolean;
  nowMs: number;
  clock?: () => number;
  title: string;
  press: { 'aria-disabled': true | undefined; onClick: () => void };
}) {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const now = clock ? Math.max(nowMs, clock()) : nowMs;
  const left = repeatSecondsLeft(availableAtMs, now);
  // At zero the wait is over and only the panel's wake is outstanding: read
  // the final label (still unavailable until that wake), never "in 0s".
  const counting = !canRepeat && left > 0;
  useEffect(() => {
    if (canRepeat || !clock || availableAtMs <= now) return;
    const timer = setTimeout(tick, (availableAtMs - now) % 1_000 || 1_000);
    return () => clearTimeout(timer);
  });
  return (
    <button
      type="button"
      className={`${ACTION_BUTTON} min-w-[20ch] justify-center tabular-nums text-amber-400 hover:bg-amber-500/10 light:text-amber-700`}
      title={title}
      aria-label={counting ? `Repeat cancel of ${label}, available in ${left}s` : `Repeat cancel of ${label}`}
      {...press}
    >
      {counting ? `Repeat cancel in ${left}s` : 'Repeat cancel'}
    </button>
  );
}

/** The reason behind a row's mark or message, in plain words, with the raw
 * code in brackets for the hover: never the bare code in a cell. */
export const usOrderCodeTitle = (code: string) => `${usOrderReasonSentence(code)} [${code}]`;

/** A row's actions and their states (pending, result, failure). The one
 * implementation every US order layout uses (this table, and the Tracker's
 * card view, #1006), so no layout can drift from the cancel rules. */
export function UsOrderRowActions({ row, actions }: { row: UsOrderRow; actions: UsOrderActions }) {
  const key = usOrderKey(row);
  const cancel = usCancelView(row, actions.readiness, actions.nowMs);
  const busy = actions.busy;
  const mine = actions.pending?.key === key ? actions.pending.kind : null;
  const error = actions.errors.get(key);
  const label = row.target.label;
  // Another row's action holds this row's buttons: they say so on hover, not
  // on a line of their own.
  const waiting = busy && !mine ? WAITING_TITLE : undefined;

  // When this row's action settles, its button may be gone (Cancel after an
  // acknowledgement). If focus fell to the page, put it back on this row.
  const container = useRef<HTMLDivElement>(null);
  const wasMine = useRef(mine);
  useEffect(() => {
    const settled = wasMine.current !== null && mine === null;
    wasMine.current = mine;
    if (!settled) return;
    const active = document.activeElement;
    if (active && active !== document.body && document.contains(active)) return;
    container.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [mine]);

  const press = (kind: UsOrderOperation, unavailable: boolean) => ({
    'aria-disabled': unavailable || undefined,
    onClick: () => {
      if (!unavailable) actions.run(kind, row);
    },
  });

  return (
    <div ref={container} className="mt-1 space-y-1" data-testid="us-order-actions">
      {/* Announced to screen readers as they change: pending, result, failure.
          Amber is the dark-theme shade; light:text-amber-700 keeps these
          warnings readable on a light background. */}
      <div aria-live="polite" className="space-y-1">
      {cancel.status && (
        <div
          className={cancel.status === 'Cancel unconfirmed' ? 'text-amber-400 light:text-amber-700' : 'text-foreground'}
          data-testid="us-order-cancel-status"
        >
          {cancel.status}
        </div>
      )}
      {mine && <div className="text-muted-foreground" data-testid="us-order-pending">{PENDING_LABEL[mine]}</div>}
      {/* #1006: an unknown cancel outcome is not a failure and not a cancel;
          the relay answers every uncertain or refused cancel this way. The
          words are the trader's; the code is the hover. */}
      {error && (
        <div className="text-amber-400 whitespace-normal light:text-amber-700" data-testid="us-order-error" title={usOrderCodeTitle(error)}>
          {isUsCancelUnconfirmedCode(error) ? US_CANCEL_UNCONFIRMED_TEXT : `Last action failed: ${usOrderReasonSentence(error)}`}
        </div>
      )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {cancel.canCancel && (
          <button
            type="button"
            className={`${ACTION_BUTTON} text-muted-foreground hover:bg-red-500/10 hover:text-red-400`}
            aria-label={`Cancel ${label}`}
            title={waiting}
            {...press('cancel', busy)}
          >
            Cancel
          </button>
        )}
        {cancel.canCheckStatus && (
          <button
            type="button"
            className={`${ACTION_BUTTON} text-muted-foreground hover:bg-muted hover:text-foreground`}
            aria-label={`Check status of ${label}`}
            title={waiting ?? CHECK_STATUS_TITLE}
            {...press('status', busy)}
          >
            Check status
          </button>
        )}
        {cancel.repeatAvailableAtMs !== null && (
          <RepeatCancelButton label={label} availableAtMs={cancel.repeatAvailableAtMs} canRepeat={cancel.canRepeatCancel}
            nowMs={actions.nowMs} clock={actions.clock} title={waiting ?? REPEAT_CANCEL_TITLE}
            press={press('repeat', busy || !cancel.canRepeatCancel)} />
        )}
      </div>
      {!cancel.canCancel && cancel.blockedReason && (
        <div className="text-muted-foreground whitespace-normal text-[10px]" data-testid="us-order-blocked">
          {cancel.blockedReason}
        </div>
      )}
    </div>
  );
}

/** The side badge, with the small "desk" word a teammate's order carries (the
 * Tracker lists only your own; Org Detail shows the desk's). */
export function UsOrderSideBadge({ row }: { row: UsOrderRow }) {
  const yes = row.target.outcome === 'yes';
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
          yes ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}
      >
        {row.action.toUpperCase()} {row.target.outcome.toUpperCase()}
      </span>
      {row.ownership === 'desk' && (
        <span className="text-muted-foreground text-[10px]" title="A teammate's order on this shared account">desk</span>
      )}
    </span>
  );
}

/**
 * The row's one-word mark, amber or muted, beside its actions (what the trader
 * needs next to Cancel). Nothing for a resting order. The reason behind an
 * unconfirmed or refused result is the hover, in plain words with its code.
 */
export function UsOrderStatusBadge({ view, className = '' }: { view: UsOrderView; className?: string }) {
  const badge = view.statusBadge;
  if (!badge) return null;
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
        badge.tone === 'amber' ? 'bg-amber-400/20 text-amber-300 light:text-amber-700' : 'bg-muted text-muted-foreground'
      } ${className}`}
      data-testid="us-order-status"
      title={view.statusReason ? usOrderCodeTitle(view.statusReason) : [view.status, view.fillStatus].filter(Boolean).join(' · ')}
    >
      {badge.text}
    </span>
  );
}

const DASH = '—';

/** Ordered size, with the venue's filled and remaining sizes beneath once a
 * lifecycle exists; dashes before that (never a word for missing evidence). */
export function UsOrderQty({ row, view, align = 'right' }: { row: UsOrderRow; view: UsOrderView; align?: 'right' | 'left' }) {
  const life = row.lifecycle;
  return (
    <>
      <div>{view.ordered}</div>
      <div className={`text-muted-foreground text-[10px] ${align === 'right' ? 'text-right' : ''}`}
        title={life ? 'Filled and remaining, as the venue last reported them' : 'The venue has not reported fills for this order yet'}>
        filled <span data-testid="us-order-filled">{life ? view.filled : DASH}</span>
        {' · '}
        remaining <span data-testid="us-order-remaining">{life ? view.remaining : DASH}</span>
      </div>
    </>
  );
}

/** The price in cents, as every venue's row shows it; the exact USD per contract is the hover. */
export function UsOrderPrice({ view }: { view: UsOrderView }) {
  return <span title={`USD per contract: ${formatUsUsd(view.price)}`}>{usPriceCentsLabel(view.price)}</span>;
}

/** The recorded fee as dollars, a rebate with its sign, a dash when none is
 * recorded. What qualifies it (rebate, when its evidence was recorded, evidence
 * too incomplete to give a figure) is the hover. */
export function usOrderFeeTitle(row: UsOrderRow): string {
  const notes: string[] = [];
  if (row.feesUsd === null) {
    notes.push(row.feeEvidence?.reasons.length ? 'Incomplete or conflicting fee evidence' : 'No fee recorded');
  } else {
    notes.push(row.feesUsd.startsWith('-') ? 'Rebate' : 'Recorded fee');
  }
  if (row.feeEvidence?.observedAtMs != null) notes.push(`recorded ${formatRecordedUtc(row.feeEvidence.observedAtMs)} UTC`);
  return notes.join(' · ');
}

export function UsOrderFees({ row, view }: { row: UsOrderRow; view: UsOrderView }) {
  return (
    <span data-testid="us-order-fees" title={usOrderFeeTitle(row)}>{row.feesUsd === null ? DASH : view.fees}</span>
  );
}

/** The US orders table's header row; also heads a pinned order (#1006). */
export function UsOrderTableHead() {
  return (
    <thead>
      <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
        <th className="px-4 py-2 text-left">Market</th>
        <th className="w-12 px-2 py-2 text-center">Source</th>
        <th className="px-3 py-2 text-center">Side</th>
        <th className="px-3 py-2 text-center">Type</th>
        <th className="px-3 py-2 text-right" title="Ordered contracts; filled and remaining beneath, as the venue reports them">Qty</th>
        <th className="px-3 py-2 text-right" title="Cents per contract of the selected outcome">Price</th>
        <th className="px-3 py-2 text-right" title="Recorded execution fees; a rebate is negative">Fees</th>
        <th className="px-2 py-2" />
      </tr>
    </thead>
  );
}

/** One US order table row. */
export function UsOrderTableRow({ row, actions, eventStartMs }: {
  row: UsOrderRow; actions?: UsOrderActions;
  /** R8-F2: the event start the host shows for this market (GTS and the
   * expiry-before-start marker read against it); absent = unknown. */
  eventStartMs?: number | null;
}) {
  const view = presentUsOrder(row);
  return (
    <tr className="border-border border-b last:border-b-0"
      data-testid="us-order-row" data-order-key={view.key} data-intent-id={row.intentId} data-order-id={row.orderId ?? undefined}>
      {/* The only wrapping cell: a long market label wraps inside a bounded
          column so the figures to its right stay on screen. */}
      <td className="text-foreground max-w-[24rem] whitespace-normal px-4 py-2 font-mono text-xs">
        <div className="break-words">{row.target.label}</div>
      </td>
      <td className="px-2 py-2 text-center">
        <PortfolioVenueBadge venue="polymarket_us" />
      </td>
      <td className="px-3 py-2 text-center">
        <UsOrderSideBadge row={row} />
      </td>
      <td className="px-3 py-2 text-center text-xs">
        <UsOrderTerms row={row} eventStartMs={eventStartMs} />
      </td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
        <UsOrderQty row={row} view={view} />
      </td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
        <UsOrderPrice view={view} />
      </td>
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
        <UsOrderFees row={row} view={view} />
      </td>
      {/* #1006: the table is whitespace-nowrap, so an action message must wrap
          here or it widens the whole table and crowds Market. */}
      <td className="min-w-[10rem] max-w-[16rem] whitespace-normal px-2 py-2 text-xs">
        <UsOrderStatusBadge view={view} />
        {actions && <UsOrderRowActions row={row} actions={actions} />}
      </td>
    </tr>
  );
}

export function UsOrderTable({ orders, actions, eventStartMs }: {
  orders: readonly UsOrderRow[];
  actions?: UsOrderActions;
  /** R8-F2: the host's event start for a row's market; absent = unknown. */
  eventStartMs?: (row: UsOrderRow) => number | null;
}) {
  if (orders.length === 0) {
    return <p className="text-muted-foreground px-4 py-3 text-xs">No open orders</p>;
  }
  return (
    // whitespace-nowrap + overflow-x-auto, as on the Tracker: columns keep the
    // width their content needs instead of wrapping rows to triple height.
    <div className="overflow-x-auto">
      <table className="w-full whitespace-nowrap" aria-label="Polymarket US orders">
        <UsOrderTableHead />
        <tbody>
          {orders.map((row) => (
            <UsOrderTableRow key={usOrderKey(row)} row={row} actions={actions} eventStartMs={eventStartMs?.(row)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
